import { useQuery } from '@apollo/client/react';
import { DataTable } from '@optools/components/data-table/data-table';
import { DataTableAddButton } from '@optools/components/data-table/data-table-add-button';
import { DataTableColumnHeader } from '@optools/components/data-table/data-table-column-header';
import { ScrollPage } from '@optools/components/layout/layout';
import { PageLoader } from '@optools/components/loading/page-loader';
import { Button } from '@optools/components/ui/button';
import { GET_VIEWING_POLICIES } from '@optools/services/operator-gateway/esni/queries';
import { ViewingPolicy } from '@optools/services/operator-gateway/types/generated/graphql';
import { CreatePolicySheet } from '@optools/tools/esni/components/create-policy-sheet';
import { EsniDetailsSheet } from '@optools/tools/esni/components/esni-details-sheet';
import { useDetailsParams } from '@optools/tools/esni/hooks/useDetailsParams';
import { createFileRoute } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { List } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute('/_auth/esni/policies')({
  component: RouteComponent,
});

export function RouteComponent() {
  const [editSheetOpen, setEditSheetOpen] = useState<boolean>(false);
  const [createSheetOpen, setCreateSheetOpen] = useState<boolean>(false);
  const { audienceHref, clearDetailsParam, setDetailsParam, viewingPolicyName } =
    useDetailsParams();

  const { data, loading } = useQuery(GET_VIEWING_POLICIES);

  const [selectedViewingPolicy, setSelectedViewingPolicy] = useState<ViewingPolicy | undefined>();

  const onEditSheetClose = () => {
    clearDetailsParam();
    setEditSheetOpen(false);
  };

  const onSelectViewingPolicy = useCallback(
    (viewingPolicy: ViewingPolicy) => {
      setSelectedViewingPolicy(viewingPolicy);
      setDetailsParam('policy', viewingPolicy.name);
      setEditSheetOpen(true);
    },
    [setDetailsParam],
  );

  useEffect(() => {
    if (viewingPolicyName) {
      const viewingPolicy = data?.viewingPolicies.find(
        (policy) => policy.name === viewingPolicyName,
      );

      if (viewingPolicy) {
        setSelectedViewingPolicy(viewingPolicy);
        setEditSheetOpen(true);
      }
    }
  }, [data?.viewingPolicies, viewingPolicyName]);

  const columns = useMemo<ColumnDef<ViewingPolicy>[]>(() => {
    return [
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Policy Name" />,
        id: 'Policy Name',
        sortingFn: (rowA, rowB) => {
          return rowA.original.name.localeCompare(rowB.original.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        },
      },
      {
        accessorKey: 'href',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Href" />,
        id: 'Href',
      },
      {
        accessorKey: 'action',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
        id: 'Action',
      },
      {
        cell: ({ cell }) => {
          return (
            <div className="mr-4 flex justify-end">
              <Button
                aria-label="policy details"
                size="icon-sm"
                title="Policy Details"
                variant="outline"
                onClick={() => onSelectViewingPolicy(cell.row.original)}>
                <List />
              </Button>
            </div>
          );
        },
        id: 'details',
      },
    ];
  }, [onSelectViewingPolicy]);

  if (loading) return <PageLoader />;

  return (
    <ScrollPage>
      <DataTable columns={columns} data={data?.viewingPolicies ?? []}>
        <DataTableAddButton onClick={() => setCreateSheetOpen(true)}>
          Add Viewing Policy
        </DataTableAddButton>
      </DataTable>

      <EsniDetailsSheet
        audienceHref={audienceHref}
        open={editSheetOpen}
        viewingPolicy={selectedViewingPolicy}
        onOpenChange={onEditSheetClose}
      />

      <CreatePolicySheet open={createSheetOpen} onOpenChange={setCreateSheetOpen} />
    </ScrollPage>
  );
}
